import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { propertyService } from '../../services/propertyService';
import PropertyGrid from '../../components/property/PropertyGrid';
import PropertyMobileListCard from '../../components/property/PropertyMobileListCard'; 
import { LayoutGrid, List, Edit, Trash2, Plus, Filter } from 'lucide-react';

const PropertyList = () => {
  const [properties, setProperties] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [viewMode, setViewMode] = useState('table'); // 'table' or 'grid'
  const [isSmallScreen, setIsSmallScreen] = useState(false); // New state for screen size

  const limit = 10;
  const navigate = useNavigate();

  useEffect(() => {
    fetchProperties();
  }, [page, filters]);

  useEffect(() => {
    const checkScreenSize = () => {
      setIsSmallScreen(window.innerWidth < 640); 
    };

    checkScreenSize(); 
    window.addEventListener('resize', checkScreenSize); 

    return () => window.removeEventListener('resize', checkScreenSize); 
  }, []);

  const fetchProperties = async () => {
    try {
      setIsLoading(true);
      const result = await propertyService.getAll({
        limit,
        offset: (page - 1) * limit,
        ...filters,
      });
      setProperties(result.properties);
      setTotal(result.total);
    } catch (error) {
      console.error('Error fetching properties:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFilterChange = (name, value) => {
    setFilters((prev) => ({
      ...prev,
      [name]: value === 'all' ? undefined : value,
    }));
    setPage(1); 
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this property?')) {
      return;
    }

    try {
      await propertyService.delete(id);
      await fetchProperties(); 
    } catch (error) {
      console.error('Error deleting property:', error);
    }
  };

  const totalPages = Math.ceil(total / limit);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-[400px] bg-white dark:bg-gray-900 rounded-lg shadow-md p-6">
        <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-b-4 border-accent dark:border-accent"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 px-2 md:p-6 bg-gray-50 dark:bg-gray-950 min-h-screen">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-5">
        <div>
          <h1 className="text-3xl font-extrabold text-gray-900 dark:text-white">Property Listings</h1>
          <p className="text-md text-gray-600 dark:text-gray-400">Effortlessly manage your real estate portfolio.</p>
        </div>
        <div className="flex items-center space-x-3">
          <div className="flex items-center space-x-0 bg-white dark:bg-gray-900 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-1">
            {!isSmallScreen && ( 
              <button
                onClick={() => setViewMode('table')}
                className={`p-2 rounded-md transition-colors duration-200 ease-in-out flex items-center gap-1
                  ${viewMode === 'table' ? 'bg-accent text-white shadow-md' : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'}
                `}
                title="Table View"
              >
                <List className="w-5 h-5" />
                <span className="hidden sm:inline">Table</span>
              </button>
            )}
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 rounded-md transition-colors duration-200 ease-in-out flex items-center gap-1
                ${viewMode === 'grid' ? 'bg-accent text-white shadow-md' : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'}
              `}
              title="Grid View"
            >
              <LayoutGrid className="w-5 h-5" />
              <span className="hidden sm:inline">Grid</span>
            </button>
          </div>
          <Link
            to="/aadashmin/properties/new"
            className="flex items-center gap-2 px-1 py-2 bg-accent text-white font-semibold rounded-lg shadow-md
                          hover:bg-accent transition-colors duration-200 ease-in-out
                          focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2"
          >
            <Plus className="h-5 w-5" />
            Add Property
          </Link>
        </div>
      </div>

      <div className="bg-white dark:bg-gray-900 p-5 rounded-lg shadow-md border border-gray-200 dark:border-gray-700">
        <h2 className="text-xl font-semibold text-gray-800 dark:text-white mb-4 flex items-center gap-2">
          <Filter className="w-5 h-5 text-accent dark:text-accent" />
          Quick Filters
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          <div>
            <label htmlFor="property-type" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Property Type</label>
            <select
              id="property-type"
              className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-700 shadow-sm focus:border-accent dark:focus:border-accent focus:ring-accent dark:focus:ring-accent py-2 px-3 text-base bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200"
              value={filters.type || 'all'}
              onChange={(e) => handleFilterChange('type', e.target.value)}
            >
              <option value="all">All Types</option>
              <option value="villa">Villa</option>
              <option value="house">House</option>
              <option value="commercial">Commercial</option>
              <option value="apartment">Apartment</option>
              <option value="studio">Studio</option>
              <option value="duplex">Duplex</option>
              <option value="land">Land</option>
            </select>
          </div>
          <div>
            <label htmlFor="property-status" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Status</label>
            <select
              id="property-status"
              className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-700 shadow-sm focus:border-accent dark:focus:border-accent focus:ring-accent dark:focus:ring-accent py-2 px-3 text-base bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200"
              value={filters.status || 'all'}
              onChange={(e) => handleFilterChange('status', e.target.value)}
            >
              <option value="all">All Status</option>
              <option value="available">For Sale</option>
              <option value="rented">For Rent</option>
              <option value="sold">Sold</option>
            </select>
          </div>
        </div>
      </div>

      {properties.length > 0 ? (
        // Conditional rendering based on screen size and viewMode
        isSmallScreen ? (
          <div className="grid grid-cols-1 gap-4">
            {properties.map((property) => (
              <PropertyMobileListCard key={property.id} property={property} onDelete={handleDelete} />
            ))}
          </div>
        ) : (
          viewMode === 'grid' ? (
            <PropertyGrid properties={properties} onDelete={handleDelete} />
          ) : (
            <div className="bg-white dark:bg-gray-900 shadow-md rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700">
              <div className="overflow-x-auto"> 
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-100 dark:bg-gray-800">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                        Property
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                        Type
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                        Price
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-100 dark:divide-gray-800">
                    {properties.map((property) => (
                      <tr key={property.id} className="hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors duration-150">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            {property.images && property.images[0] && (
                              <img
                                src={property.images[0]?.url}
                                alt={property.title}
                                className="h-12 w-12 rounded-lg object-cover mr-4 border border-gray-200 dark:border-gray-700 shadow-sm"
                              />
                            )}
                            <div>
                              <div className="text-base font-semibold text-gray-900 dark:text-white">
                                {property.title}
                              </div>
                              <div className="text-sm text-gray-600 dark:text-gray-400">
                                <span className="font-medium">{property.bedrooms}Beds</span> · <span className="font-medium">{property.bathrooms}Baths</span>
                              </div>
                              <div className="text-sm text-gray-500 dark:text-gray-500">{property.location}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-800 dark:text-gray-200 capitalize">
                          {property.property_type}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold
                              ${property.status === 'available'
                                ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300'
                                : property.status === 'rented'
                                ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300'
                                : property.status === 'sold'
                                ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300'
                                : 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300'
                              } capitalize`}
                          >
                            {property.status === 'available' ? 'For Sale' : property.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-base font-semibold text-gray-900 dark:text-white">
                          ₹ {property.price.toLocaleString()}
                        </td>
                        <td className="px-6 py-4 text-right text-sm font-medium space-x-2">
                          <button
                            onClick={() => navigate(`/aadashmin/properties/${property.id}/edit`)}
                            className="text-accent hover:text-accent dark:text-accent dark:hover:text-accent p-2 rounded-full hover:bg-accentdark:hover:bg-accent transition-colors duration-200"
                            title="Edit Property"
                          >
                            <Edit className="h-5 w-5" />
                          </button>
                          <button
                            onClick={() => handleDelete(property.id)}
                            className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-200 p-2 rounded-full hover:bg-red-50 dark:hover:bg-red-900 transition-colors duration-200"
                            title="Delete Property"
                          >
                            <Trash2 className="h-5 w-5" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )
        )
      ) : (
        <div className="bg-white dark:bg-gray-900 shadow-md rounded-lg p-8 text-center border border-gray-200 dark:border-gray-700">
          <p className="text-lg text-gray-600 dark:text-gray-300 mb-4">
            No properties found for the current filters.
          </p>
          <p className="text-md text-gray-500 dark:text-gray-400">
            Try adjusting your filters or click "Add Property" to create a new listing.
          </p>
          <Link
            to="/aadashmin/properties/new"
            className="inline-flex items-center gap-2 mt-6 px-6 py-3 bg-accent text-white font-semibold rounded-lg shadow-md
                          hover:bg-accent transition-colors duration-200 ease-in-out
                          focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2"
          >
            <Plus className="h-5 w-5" />
            Add Your First Property
          </Link>
        </div>
      )}

      {totalPages > 1 && (
        <div className="flex justify-center items-center space-x-4 pt-4">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            className="px-4 py-2 border border-gray-300 dark:border-gray-700 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300
                          hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed
                          transition-colors duration-200"
          >
            Previous
          </button>
          <span className="text-md text-gray-700 dark:text-gray-300 font-medium">
            Page {page} of {totalPages} of {total} properties
          </span>
          <button
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            className="px-4 py-2 border border-gray-300 dark:border-gray-700 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300
                          hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed
                          transition-colors duration-200"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default PropertyList;